import type { ReactNode } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PermissionRoute } from "@/components/auth/PermissionRoute";
import { RequirePlan } from "@/components/common/Gated";
import { anyOfForPath, featureForPath } from "@/rbac/screenCatalog";
import type { EntitlementKey } from "@/hooks/usePlanEntitlements";

export interface StaffGateProps {
  /** The screen's catalog path (e.g. "/crm/customers/:customerId"). */
  path: string;
  children: ReactNode;
  /** The route sits inside a layout shell (Settings, Reports). */
  nested?: boolean;
  /** Entitlement to require; defaults to the catalog screen's `feature`. */
  feature?: EntitlementKey;
}

/**
 * A staff screen: signed-in staff session, then the catalog's permissions,
 * then its entitlement (e.g. CRM, which can be switched off per outlet).
 */
export function StaffGate({ path, children, nested, feature }: StaffGateProps) {
  const anyOf = anyOfForPath(path);
  if (!anyOf?.length) {
    throw new Error(`No screen catalog match for ${path}`);
  }
  const requiredFeature = feature ?? featureForPath(path);
  const inner = requiredFeature ? (
    <RequirePlan feature={requiredFeature} nested={nested}>
      {children}
    </RequirePlan>
  ) : (
    children
  );
  const gated = (
    <PermissionRoute anyOf={anyOf} nested={nested}>
      {inner}
    </PermissionRoute>
  );
  if (nested) return gated;
  return <ProtectedRoute>{gated}</ProtectedRoute>;
}
